import {
  Box,
  Card,
  Flex,
  Icon,
  Text,
  Button,
  Heading,
  FormLabel,
  FormControl,
  CurrencyInput
} from '@homebotapp/hb-react-component-catalog'
import { useState } from 'react'
import { useIntl, defineMessages } from 'react-intl'
import { formatCurrency } from '../../../../../../helpers/formatters'

export const MSG = defineMessages({
  hoaSpelledOut: {
    id: 'EstimatedMonthlyPaymentBreakdown.hoaSpelledOut',
    defaultMessage: "Homeowner's Association"
  },
  hoaAbbreviation: {
    id: 'EstimatedMonthlyPaymentBreakdown.hoaAbbreviation',
    defaultMessage: 'HOA'
  },
  hoaFees: {
    id: 'EstimatedMonthlyPaymentBreakdown.hoaFees',
    defaultMessage: '{hoa} fees'
  },
  hoaFeesDescription: {
    id: 'EstimatedMonthlyPaymentBreakdown.hoaFeesDescription',
    defaultMessage: 'Based on listing information'
  },
  monthlyAmount: {
    id: 'EstimatedMonthlyPaymentBreakdown.monthlyAmount',
    defaultMessage: 'Monthly amount'
  },
  show: {
    id: 'EstimatedMonthlyPaymentBreakdown.hoaShow',
    defaultMessage: 'Show editable fields'
  },
  hide: {
    id: 'EstimatedMonthlyPaymentBreakdown.hoaHide',
    defaultMessage: 'Hide editable fields'
  }
})
export interface EstimatedMonthlyPaymentBreakdownHoaFeesProps {
  hoaFees: string
  isCalculatorsPage?: boolean
  setHoaFees: (value: string) => void
  setHasUsedMortgageCalculator: (value: boolean) => void
}

export const EstimatedMonthlyPaymentBreakdownHoaFees = ({
  hoaFees,
  setHoaFees,
  isCalculatorsPage,
  setHasUsedMortgageCalculator
}: EstimatedMonthlyPaymentBreakdownHoaFeesProps) => {
  const intl = useIntl()
  const [showEditableFields, setShowEditableFields] = useState(false)

  return (
    <Card p={3}>
      <Button
        w='100%'
        variant='link'
        display='block'
        textAlign='left'
        fontWeight='inherit'
        textColor='currentColor'
        _hover={{ textDecoration: 'none' }}
        onClick={() => setShowEditableFields(!showEditableFields)}
        tracking={{
          guid: '7Ka47U8jCrLGA4Mu2kZrVb',
          ui_context: isCalculatorsPage ? 'Mortgage Calculator Page' : 'Listing Details Page Mortgage Calculator',
          descriptor: `HOA Fees ${showEditableFields ? 'Hide' : 'Show'}`
        }}
        aria-label={intl.formatMessage(showEditableFields ? MSG.hide : MSG.show)}
      >
        <Flex align='center' justify='space-between'>
          <Flex gap={2} align='center'>
            <Box bg='primary.900' width={3} height={3} borderRadius='50%' />
            <Heading as='h4' size={['2xs', 'xs', 'sm']}>
              {intl.formatMessage(MSG.hoaFees, {
                hoa: (
                  <span>
                    <Text
                      as='span'
                      fontWeight='inherit'
                      size='inherit'
                      fontStyle='inherit'
                      display={['initial', 'none']}
                    >
                      <abbr title={intl.formatMessage(MSG.hoaSpelledOut)} style={{ textDecoration: 'none' }}>
                        {intl.formatMessage(MSG.hoaSpelledOut)}
                      </abbr>
                    </Text>
                    <Text
                      as='span'
                      fontWeight='inherit'
                      size='inherit'
                      fontStyle='inherit'
                      display={['none', 'initial']}
                    >
                      <span>
                        {intl.formatMessage(MSG.hoaSpelledOut)}{' '}
                        <Text
                          as='span'
                          fontWeight='inherit'
                          size='inherit'
                          fontStyle='inherit'
                          display={['none', 'none', 'initial']}
                        >
                          ({intl.formatMessage(MSG.hoaAbbreviation)})
                        </Text>
                      </span>
                    </Text>
                  </span>
                )
              })}
            </Heading>
          </Flex>
          <Flex align='center' gap={1}>
            <Text
              mb={0}
              color='neutral.900'
              fontWeight='semibold'
              size={['xs', 'sm', 'md']}
              style={{ fontVariant: 'tabular-nums' }}
            >
              {formatCurrency(hoaFees)}
            </Text>
            <Icon
              height={2}
              name='play'
              transform={`rotate(${showEditableFields ? -90 : 90}deg)`}
              color='neutral.900'
            />
          </Flex>
        </Flex>
        {!isCalculatorsPage && (
          <Text ml={5} as='span' size={['2xs', 'xs', 'sm']} display='block'>
            {intl.formatMessage(MSG.hoaFeesDescription)}
          </Text>
        )}
      </Button>
      {showEditableFields && (
        <FormControl mt={4}>
          <FormLabel size='xs'>{intl.formatMessage(MSG.monthlyAmount)}</FormLabel>
          <CurrencyInput
            size='sm'
            name='hoaFees'
            maxWidth='250px'
            decimalScale={2}
            defaultValue={hoaFees}
            // @ts-ignore
            onChange={val => setHoaFees(val)}
            onFocus={() => setHasUsedMortgageCalculator(true)}
            tracking={{
              descriptor: 'HOA Fees',
              guid: 'oRxvQ6q3vy4uuw7LGYKtgZ',
              ui_context: isCalculatorsPage ? 'Mortgage Calculator Page' : 'Listing Details Page Mortgage Calculator'
            }}
          />
        </FormControl>
      )}
    </Card>
  )
}
